import { I18n } from "../../../hooks/useTranslations";
import {
  CurveIntermediateForSettings,
  MaterialForSettings,
  PatrolNormativeForSettings,
  PatrolSeamHeight,
  PatrolSettings,
  PatrolSystemClampingForSettings,
  PatrolSystemForSettings,
  PatrolSystemMappingForSettings,
  RoofCategoryForSettings,
  RoofMaterialForSettings,
  RoofSubstructureForSettings,
  RoofTypeForSettings,
  ShuttleForSettings,
  SupportExtraForSettings,
  SupportForSettings,
  SupportOptionalForSettings,
  SystemTypeForSettings,
} from "../../../http/patrol/types/patrolSettings";
import { RoofSubstructureSize } from "../../../http/patrol/types/roofSubstructures";
import { getAngleDegrees } from "../../../mapUtils";
import { PatrolRoofInfoFormBody } from "./PatrolRoofInfo";
import { EdgeData, LIFE_LINE_MIN_INTERMEDIATE_DEGREES } from "./useLifeLineDrawer";

export const getIntermediateLabel = (curveIntermediate: CurveIntermediateForSettings) =>
  curveIntermediate.item.code + (curveIntermediate.extraItem ? `+${curveIntermediate.extraItem?.code}` : "");

export const getSystemLabel = (i18n: I18n, x: PatrolSystemForSettings, clamped: boolean) =>
  `${x.name} ${clamped ? `(${i18n.translation.common.clamped})` : ""}`;

export const getClampingLabel = (itemCode: string, x: PatrolSystemClampingForSettings) => {
  const parts: string[] = [];

  if (x.includeSelectedComponent) {
    parts.push(itemCode);
  }

  if (x.topItem?.code) {
    parts.push(x.topItem.code);
  }

  if (x.topExtraItem?.code) {
    parts.push(x.topExtraItem.code);
  }

  return parts.join(" + ");
};

export const getLifeLineOptionals = (
  system: PatrolSystemForSettings | undefined | null,
  supportExtras?: SupportExtraForSettings[]
): SupportOptionalForSettings[] => {
  const supportExtrasIds = supportExtras?.map((x) => x.id) ?? [];
  return (
    system?.supportOptionals?.filter(
      (x) => x.forLifeLines && (!x.supportExtraDependency || supportExtrasIds.includes(x.supportExtraDependency.id))
    ) ?? []
  );
};

export const getLifelineSupportExtras = ({
  supportExtras,
  roofSubstructure,
  insulationThickness,
  substructureHeight,
  seamHeight,
}: {
  supportExtras?: SupportExtraForSettings[] | null;
  roofSubstructure?: RoofSubstructureForSettings | null;
  insulationThickness?: number | null;
  substructureHeight?: number | null;
  seamHeight?: number | null;
}) =>
  supportExtras?.filter((x) => {
    // consider only extras for life lines
    if (!x.forLifeLines) {
      return false;
    }

    // if there is a substructure limitation
    if (roofSubstructure && x.roofSubstructure && x.roofSubstructure?.id !== roofSubstructure.id) {
      return false;
    }

    // based on the thickness of insulation and substructure
    if (insulationThickness && (x.minThickness || x.maxThickness)) {
      const min = x.minThickness ?? 0;
      const max = x.maxThickness ?? Infinity;
      const thickness = substructureHeight ?? 0 + insulationThickness;
      if (thickness < min || thickness > max) {
        return false;
      }
    }

    if (seamHeight && x.seamHeight && seamHeight !== x.seamHeight) {
      return false;
    }

    return true;
  }) ?? [];

export const getSupports = ({
  system,
  seamsSpan,
  supportMaterial,
  insulationThickness,
  roofCategory,
  normative,
}: {
  system?: PatrolSystemForSettings | null;
  seamsSpan?: number | null;
  supportMaterial?: MaterialForSettings | null;
  insulationThickness?: number | null;
  roofCategory?: RoofCategoryForSettings | null;
  normative?: PatrolNormativeForSettings | null;
}) => {
  return system?.supports?.filter((x) => {
    if (x.maxSeamsSpan && seamsSpan && seamsSpan > x.maxSeamsSpan) {
      return false;
    }

    if (normative?.excludedItems?.map((i) => i.item.id).includes(x.item.id)) {
      return false;
    }
    if (x.maxInsulationThickness && insulationThickness && insulationThickness > x.maxInsulationThickness) {
      return false;
    }

    if (roofCategory?.isFlat && !x.flatRoofCompatible) {
      return false;
    }

    if (x.supportMaterial && x.supportMaterial.id !== supportMaterial?.id) {
      return false;
    }

    return true;
  });
};

export const getMinSubstructureHeight = (
  substructure: RoofSubstructureForSettings | null,
  roofMaterialSystemMappings?: PatrolSystemMappingForSettings[]
) =>
  roofMaterialSystemMappings?.reduce((rv: number | null, x) => {
    if (
      (x.minSubstructureHeight || x.minSubstructureHeight === 0) &&
      ((!x.roofSubstructureId && !substructure) || x.roofSubstructureId === substructure?.id)
    ) {
      return Math.min(rv ?? Infinity, x.minSubstructureHeight);
    }

    return rv;
  }, null);

export const getMinSubstructureSize = (
  substructure: RoofSubstructureForSettings | null,
  roofMaterialSystemMappings: PatrolSystemMappingForSettings[] | undefined
) =>
  roofMaterialSystemMappings?.reduce<RoofSubstructureSize | null>((rv, x) => {
    if (
      x.minSubstructureSize &&
      ((!x.roofSubstructureId && !substructure) || x.roofSubstructureId === substructure?.id)
    ) {
      if (!rv) {
        rv = { height: x.minSubstructureSize.height, width: x.minSubstructureSize.width };
      } else {
        rv.height = Math.min(rv.height, x.minSubstructureSize.height);
        rv.width = Math.min(rv.width, x.minSubstructureSize.width);
      }
    }
    return rv;
  }, null) ?? null;

export const getMinMetalPlateThickness = (
  type: RoofTypeForSettings | null,
  material: RoofMaterialForSettings | null,
  substructure: RoofSubstructureForSettings | null,
  systemMappings?: PatrolSystemMappingForSettings[]
) =>
  systemMappings?.reduce((rv: number | null, x) => {
    if (
      (x.minMetalPlateThickness || x.minMetalPlateThickness === 0) &&
      x.roofCategoryId === type?.roofCategory?.id &&
      ((!x.roofMaterialId && !material) || x.roofMaterialId === material?.id) &&
      ((!x.roofSubstructureId && !substructure) || x.roofSubstructureId === substructure?.id)
    ) {
      return Math.min(rv ?? Infinity, x.minMetalPlateThickness);
    }

    return rv;
  }, null);

export const getSeamHeights = (system: PatrolSystemForSettings | null, settings?: PatrolSettings) => {
  const seen = new Set<number>();
  const seamHeights: PatrolSeamHeight[] = [];

  [...(system?.supportExtras ?? []), ...(settings?.anchorMappings ?? [])].forEach((x) => {
    if (x.seamHeight && !seen.has(x.seamHeight)) {
      seamHeights.push({ value: x.seamHeight });
      seen.add(x.seamHeight);
    }
  });
  return seamHeights;
};

export const getSystemSystemMappings = (
  systems: PatrolSystemForSettings[],
  roofMaterialSystemMappings: PatrolSystemMappingForSettings[],
  substructureSize: Partial<RoofSubstructureSize> | null,
  substructure: RoofSubstructureForSettings | null,
  substructureHeight: number | null,
  metalPlateThickness: number | null | undefined
): {
  mappings: PatrolSystemMappingForSettings[];
  clampable: PatrolSystemMappingForSettings[];
  onlyClampable: PatrolSystemMappingForSettings[];
} => {
  const systemsMap = systems.reduce((rv, s) => {
    rv.set(s.id, s);
    return rv;
  }, new Map<string, PatrolSystemForSettings>());

  const collect = (clamped: boolean) =>
    roofMaterialSystemMappings?.reduce((rv: PatrolSystemMappingForSettings[], x) => {
      const system = systemsMap.get(x.systemId);
      const substructureMatches = (!x.roofSubstructureId && !substructure) || x.roofSubstructureId === substructure?.id;

      // skip if the substructure does not match and the system is not substructure independent
      if (!system?.isSubstructureIndependent && !substructureMatches) return rv;

      const isClampable = system?.isClampable;

      // skip if substructure height not allowed and not clamped+clampable
      const heightAllowed =
        (clamped && isClampable) ||
        !x.minSubstructureHeight ||
        (substructureHeight && substructureHeight >= x.minSubstructureHeight);
      if (!heightAllowed) return rv;

      // skip if substructure size not allowed
      const sizeAllowed =
        !x.minSubstructureSize ||
        (substructureSize?.height &&
          substructureSize?.width &&
          substructureSize.height >= x.minSubstructureSize.height &&
          substructureSize.width >= x.minSubstructureSize.width);
      if (!sizeAllowed) return rv;

      // skip if metal plate thickness not allowed
      const thicknessAllowed =
        !x.minMetalPlateThickness || (metalPlateThickness && metalPlateThickness >= x.minMetalPlateThickness);
      if (!thicknessAllowed) return rv;

      rv.push(x);
      return rv;
    }, []);

  const clampable = collect(true);
  const mappings = collect(false);
  const onlyClampable = clampable.filter((x) => systemsMap.get(x.systemId)?.isClampable);

  return { mappings, clampable, onlyClampable };
};

export const getSupportMaterialIds = (supportMappings?: SupportForSettings[]) =>
  supportMappings?.reduce((rv: string[], x) => {
    if (x.supportMaterial) {
      return [...rv, x.supportMaterial.id];
    }
    return rv;
  }, []);

export const getNodeNameByType = (
  i18n: I18n,
  node?: {
    degrees: number[];
    isSplit: boolean;
  }
): string => {
  if (node?.isSplit) {
    return i18n.translation.common.terminal_plus_terminal;
  }
  if (node?.degrees.length === 3) {
    return i18n.translation.common.terminal_plus_intermediate;
  }
  if (node?.degrees.length === 0) {
    return i18n.translation.common.terminal;
  }
  if (node?.degrees.length === 1 && (node.degrees[0] ?? 0) < LIFE_LINE_MIN_INTERMEDIATE_DEGREES) {
    return i18n.translation.common.curve;
  }
  if (node?.degrees.length === 1 && Math.max(...node.degrees) >= LIFE_LINE_MIN_INTERMEDIATE_DEGREES) {
    return i18n.translation.common.intermediate;
  }
  return i18n.translation.common.error;
};

export const getAbsorbers = (componentsMaterial?: MaterialForSettings | null, roofInfo?: PatrolRoofInfoFormBody) =>
  componentsMaterial?.absorbers?.filter((x) => x.systems.some((s) => s.systemId === roofInfo?.system?.id)) ?? [];

export const getTerminals = (componentsMaterial?: MaterialForSettings | null, roofInfo?: PatrolRoofInfoFormBody) =>
  componentsMaterial?.terminals?.filter((x) => x.systemIds.some((s) => s === roofInfo?.system?.id)) ?? [];

export const getCurveIntermediates = (
  type: "curve" | "intermediate",
  curvesIntermediates?: CurveIntermediateForSettings[],
  roofInfo?: PatrolRoofInfoFormBody,
  componentsMaterial?: MaterialForSettings | null,
  systemType?: SystemTypeForSettings | null
) =>
  curvesIntermediates?.filter(
    (curveIntermediate) =>
      componentsMaterial?.id &&
      curveIntermediate.materialsIds.includes(componentsMaterial.id) &&
      systemType?.id &&
      curveIntermediate.systemTypesIds.includes(systemType.id) &&
      roofInfo?.system?.id &&
      curveIntermediate.systemsIds.includes(roofInfo.system.id) &&
      // angle >= curveIntermediate.minAngle && angle <= curveIntermediate.maxAngle
      ((type === "intermediate" && curveIntermediate.minAngle >= LIFE_LINE_MIN_INTERMEDIATE_DEGREES) ||
        (type === "curve" && curveIntermediate.maxAngle < LIFE_LINE_MIN_INTERMEDIATE_DEGREES))
  );

export const filterShuttles = (
  shuttles: ShuttleForSettings[],
  fixed: boolean,
  systemType?: SystemTypeForSettings | null,
  system?: PatrolSystemForSettings | null,
  componentsMaterial?: MaterialForSettings | null
) => {
  return shuttles?.filter(
    (s) =>
      s.fixed == fixed &&
      s.systemTypeId === systemType?.id &&
      s.systemIds.includes(system?.id ?? "") &&
      s.supportMaterialIds.includes(componentsMaterial?.id ?? "")
  );
};

/**
 * Count curves (angles > 90°) in an ordered or unordered list of edges.
 */
export const countCurves = (edges: EdgeData[]): number => {
  if (edges.length < 2) return 0;

  // --- 1️⃣ Build adjacency: nodeId -> connected edges ---
  const adj = new Map<string, EdgeData[]>();
  for (const e of edges) {
    const { startNodeId, endNodeId } = e.edge;
    if (!adj.has(startNodeId)) adj.set(startNodeId, []);
    if (!adj.has(endNodeId)) adj.set(endNodeId, []);
    adj.get(startNodeId)!.push(e);
    adj.get(endNodeId)!.push(e);
  }

  // --- 2️⃣ Find start node (a node with only one connection, or fallback) ---
  const startNode = [...adj.entries()].find(([_, list]) => list.length === 1)?.[0] ?? edges[0]?.edge.startNodeId ?? "";

  // --- 3️⃣ Traverse the path in correct order ---
  const ordered: EdgeData[] = [];
  const visited = new Set<string>();
  let currentNode = startNode;

  while (true) {
    const nextEdge = (adj.get(currentNode) || []).find((e) => !visited.has(e.edge.id));
    if (!nextEdge) break;

    visited.add(nextEdge.edge.id);
    ordered.push(nextEdge);

    // Move to the next node in the chain
    const { startNodeId, endNodeId } = nextEdge.edge;
    currentNode = startNodeId === currentNode ? endNodeId : startNodeId;
  }

  // --- 4️⃣ Compute angles between consecutive segments ---
  let curveCount = 0;

  for (let i = 1; i < ordered.length; i++) {
    const prev = ordered[i - 1];
    const curr = ordered[i];

    if (!prev || !curr) {
      continue;
    }

    // Determine the shared node between prev and curr
    const sharedNodeId =
      prev.edge.startNodeId === curr.edge.startNodeId || prev.edge.startNodeId === curr.edge.endNodeId
        ? prev.edge.startNodeId
        : prev.edge.endNodeId;

    const middle = sharedNodeId === prev.edge.startNodeId ? prev.segment.getFrom() : prev.segment.getTo();

    const point1 = sharedNodeId === prev.edge.startNodeId ? prev.segment.getTo() : prev.segment.getFrom();

    const point3 = sharedNodeId === curr.edge.startNodeId ? curr.segment.getTo() : curr.segment.getFrom();

    const angle = getAngleDegrees(point1, middle, point3);
    if (angle < LIFE_LINE_MIN_INTERMEDIATE_DEGREES) curveCount++;
  }

  return curveCount;
};

export function getLifeLineColor(lifeLineId: number, numbersOfLifeLines: number): string {
  const hue = (lifeLineId * 360) / numbersOfLifeLines;
  const color = `hsl(${hue}, 70%, 50%)`;
  return color;
}
